import type { OwnProfile } from '@molt/shared';
import type { RefObject } from 'react';
import { Avatar, profileName } from '@/components/Avatar';
import { Menu } from '@/components/Menu';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  appearanceMenuSections,
  type AppearanceMenuState,
} from './AppearanceMenuSection';
import {
  accessibilityMenuSections,
  type AccessibilityMenuState,
} from './AccessibilityMenuSection';

/**
 * The header's account slot — `FE-06.1`, `entry-surfaces.md` D1/D7.
 *
 * `docs/design/d1/D1-BRIEF.md` §6.4/§7 fix every signature and every
 * accessible name here; nothing below is this file's own taste.
 *
 * ── The trigger is a `<button>`, not `Avatar` ──────────────────────────────
 * `Avatar` never renders its own control (its own docstring: both known call
 * sites already sit inside one), so this component supplies the `<button>`
 * and passes `decorative` — the button already carries the accessible name,
 * and a `role="img"` avatar nested inside it would give the trigger two
 * competing names.
 *
 * ── The name chain, not the raw field ──────────────────────────────────────
 * `displayName`, `username` and `initials`/`initialsColour` are all legally
 * `''` on a real row (`publicProfileSchema` puts a `.max()` and no `.min()`
 * on every one of them). The brief's fixed text for the trigger reads
 * "`Your account, {displayName}`", but a caller with an empty `displayName`
 * makes that literal substitution "Your account, " — an accessible name that
 * announces nothing about who is signed in. `profileName()` is the same
 * three-deep fallback (`displayName` → `@username` → `"Member"`) `Avatar`
 * itself uses to render, exported from `Avatar.tsx` for exactly this reason:
 * one place derives the name, so this trigger and the image it wraps can
 * never disagree about whose account it is.
 *
 * ── Profile is a callback, and here is the constraint that forced it ───────
 * `MenuItem` takes `onSelect`, not `href` (`Menu.tsx`'s shape is fixed and
 * not this chunk's to change) — so `Link`-as-content was considered and
 * ruled out by measurement, not preference. Radix's own `MenuItem` confirms
 * it: keyboard activation (`Enter`/`Space`) calls `event.currentTarget.click()`
 * on the item element itself (`@radix-ui/react-menu@2.1.24`, `dist/index.mjs`
 * `MenuItem`'s `onKeyDown`), never on a descendant. A `Link` placed only
 * inside the item's `label` would therefore work for a mouse click (the
 * click bubbles up through the anchor to the item's own handler) and
 * silently do nothing on keyboard activation — worse than either pure
 * option, because it would pass a mouse-only smoke test.
 *
 * `onOpenProfile` is the fix: the route supplies real navigation
 * (`router.navigate`), and this component stays hook-free and router-free.
 * The alternative — `window.location.assign('/profile')` inside this file —
 * was shipped first and reverted: it satisfied the original fixed signature
 * but forced a full page load, which discards the `['me']` cache entry
 * brief §5.5 depends on for the account menu, header and profile agreeing
 * without a reload. `D1`'s parent ratified the signature change over
 * widening `Menu` with per-item `asChild`/`href`, because `Menu` is a `B3`
 * primitive the board face already composes and widening it from inside a
 * chunk that does not own it is exactly the scope creep block ownership
 * exists to prevent. `D2`'s workspace switcher hits this identical
 * constraint (switching workspaces is navigation through the same `Menu`)
 * and should make the same call, or fund `Menu`'s `asChild` support once,
 * upstream of both.
 */

export interface AccountMenuProps {
  profile: OwnProfile;
  appearance?: AppearanceMenuState | undefined;
  accessibility?: AccessibilityMenuState | undefined;
  triggerRef?: RefObject<HTMLButtonElement | null> | undefined;
  /**
   * Navigates to `/profile`. Required, not optional — a menu item that goes
   * nowhere is worse than no item, and this is the one navigation this
   * presentational component cannot perform for itself (see the docstring
   * above).
   */
  onOpenProfile: () => void;
  onOpenInbox: () => void;
  onOpenPlanner: () => void;
  onOpenMyCards: () => void;
  /**
   * Navigates to `/admin` — SA-9. Optional, and the item exists only when it
   * is passed: `routes/__root.tsx` passes it for a platform superadmin alone
   * (`isSuperadminSession`). The item is driven by the PROP rather than by a
   * flag read here, so this component keeps taking an `OwnProfile` and every
   * other account keeps exactly the five items it had.
   */
  onOpenAdmin?: (() => void) | undefined;
  onSignOut: () => void;
  signingOut?: boolean;
}

export function AccountMenu({
  profile,
  appearance,
  accessibility,
  triggerRef,
  onOpenProfile,
  onOpenInbox,
  onOpenPlanner,
  onOpenMyCards,
  onOpenAdmin,
  onSignOut,
  signingOut = false,
}: AccountMenuProps) {
  const { t } = useInterfaceLanguage();
  const name = profileName(profile);
  // `username === ''` is legal (no `.min()`), and a heading that is one
  // punctuation mark ("@") is noise a screen reader reads aloud for no
  // information. Omitting the heading is a quiet, legal degradation;
  // `MenuSection.heading` is optional for exactly this.
  const heading = profile.username === '' ? undefined : `@${profile.username}`;

  const sections = [
    {
      id: 'account',
      ...(heading === undefined ? {} : { heading }),
      items: [
        { id: 'inbox', label: t('account.inbox'), onSelect: onOpenInbox },
        { id: 'planner', label: t('account.planner'), onSelect: onOpenPlanner },
        { id: 'my-cards', label: t('account.myCards'), onSelect: onOpenMyCards },
        { id: 'profile', label: t('account.profile'), onSelect: onOpenProfile },
        ...(onOpenAdmin === undefined
          ? []
          : [{ id: 'admin', label: t('account.admin'), onSelect: onOpenAdmin }]),
        {
          id: 'sign-out',
          label: t('account.signOut'),
          onSelect: onSignOut,
          disabled: signingOut,
        },
      ],
    },
    ...(accessibility === undefined
      ? []
      : accessibilityMenuSections({ ...accessibility, t })),
    ...(appearance === undefined ? [] : appearanceMenuSections({ ...appearance, t })),
  ];

  return (
    <Menu
      align="end"
      trigger={
        <button
          ref={triggerRef}
          type="button"
          aria-label={t('account.trigger', { name })}
          className="flex shrink-0 items-center justify-center rounded-full"
        >
          {/*
            The hover/opacity affordance lives on `Avatar` itself
            (`interactive`), not on this button — the avatar's span fills the
            button, so stacking `hover:opacity-85` on both composites to
            ≈0.72 on hover. One affordance, one place it is declared.
          */}
          <Avatar profile={profile} size="sm" interactive decorative />
        </button>
      }
      sections={sections}
    />
  );
}
